<template>
    <div style="min-height: calc(100vh - 158px);" class="card">
        <PageHeader
            title="Review Synced Products"
            :subtitle="referenceLabel"
            @onClickCloseButton="goToList"
        >
            <template #extra-btn>
                <button
                    @click="openBulkApply"
                    class="btn page-head-btn page-head-btn-ghost"
                    :disabled="! pendingRows.length"
                >Apply To All</button>
                <button
                    @click="onApprove"
                    class="btn page-head-btn page-head-btn-primary"
                    :disabled="! readyCount"
                >Approve Ready ({{ readyCount }})</button>
            </template>
        </PageHeader>

        <div class="status-bar">
            <span class="status-chip is-awaiting">Awaiting<b>{{ pendingRows.length }}</b></span>
            <span class="status-chip is-ready">Ready<b>{{ readyCount }}</b></span>
            <span class="status-chip is-blocked">Blocked<b>{{ blockedCount }}</b></span>
            <span class="status-chip is-rejected">Rejected<b>{{ rejectedRows.length }}</b></span>
            <span class="status-chip is-approved">Already approved<b>{{ log.approved_count }}</b></span>
        </div>

        <div v-if="bulkOpen" class="bulk-panel">
            <h5 class="bulk-title">Apply to all awaiting products</h5>
            <p class="bulk-hint">Only the fields you fill in here are copied. Empty fields are left untouched.</p>
            <div class="row g-1">
                <div class="col-12 col-md-4 col-lg-3">
                    <label class="filter-label">Product Group</label>
                    <v-select v-model="bulk.product_head_id" :options="groups" label="name" :reduce="option => option.id"/>
                </div>
                <div class="col-12 col-md-4 col-lg-3">
                    <label class="filter-label">Unit</label>
                    <v-select v-model="bulk.unit_id" :options="units" label="name" :reduce="option => option.id"/>
                </div>
                <div class="col-12 col-md-4 col-lg-3">
                    <label class="filter-label">Purchase A/C</label>
                    <v-select v-model="bulk.purchase_account_head_id" :options="purchaseHeads" label="name" :reduce="option => option.id"/>
                </div>
                <div class="col-12 col-md-4 col-lg-3">
                    <label class="filter-label">Sales A/C</label>
                    <v-select v-model="bulk.sales_account_head_id" :options="salesHeads" label="name" :reduce="option => option.id"/>
                </div>
                <div class="col-12 col-md-4 col-lg-3">
                    <label class="filter-label">Cost of Sales A/C</label>
                    <v-select v-model="bulk.cost_of_sales_account_head_id" :options="costHeads" label="name" :reduce="option => option.id"/>
                </div>
                <div class="col-12 col-md-4 col-lg-3">
                    <label class="filter-label">Inventory A/C</label>
                    <v-select v-model="bulk.inventory_account_head_id" :options="inventoryHeads" label="name" :reduce="option => option.id"/>
                </div>
            </div>
            <div class="bulk-actions">
                <button @click="onApplyToAll" class="btn row-btn row-btn-primary">Apply</button>
                <button @click="closeBulkApply" class="btn row-btn row-btn-ghost">Cancel</button>
            </div>
        </div>

        <div class="cards-section">
            <div v-for="row in rows" :key="row.data.batch" class="product-card" :class="cardClass(row)">
                <div class="card-head">
                    <div class="card-head-left">
                        <div class="card-title-row">
                            <h5 class="card-title">{{ row.data.name }}</h5>
                            <span v-if="isReady(row)" class="status-chip is-ready">Ready</span>
                            <span v-if="isRejected(row)" class="status-chip is-rejected">Rejected</span>
                            <span v-if="! isReady(row) && ! isRejected(row)" class="status-chip is-blocked">Needs info</span>
                        </div>
                        <small class="card-meta">
                            Storefront ID: {{ row.data.external_product_id }} &middot; Sales price: {{ row.data.sales_price }}
                        </small>
                    </div>
                    <div class="card-head-actions">
                        <button v-if="! isRejected(row)" @click="onSave(row)" class="btn row-btn row-btn-primary">Save</button>
                        <button v-if="! isRejected(row)" @click="onReject(row)" class="btn row-btn row-btn-danger">Reject</button>
                        <button v-if="isRejected(row)" @click="onRestore(row)" class="btn row-btn row-btn-ghost">Restore</button>
                    </div>
                </div>

                <div v-if="! isRejected(row)" class="row g-2 card-fields">
                    <div class="col-12 col-md-6 col-lg-3">
                        <label class="filter-label">Product Name <span class="text-danger">*</span></label>
                        <input v-model="row.data.name" type="text" class="form-control" :class="fieldClass(row, 'name')">
                        <small v-if="errorOf(row, 'name')" class="text-danger">{{ errorOf(row, 'name') }}</small>
                    </div>
                    <div class="col-12 col-md-6 col-lg-3">
                        <label class="filter-label">Product Group <span class="text-danger">*</span></label>
                        <v-select v-model="row.data.product_head_id" :options="groups" label="name" :reduce="option => option.id"/>
                        <small v-if="errorOf(row, 'product_head_id')" class="text-danger">{{ errorOf(row, 'product_head_id') }}</small>
                    </div>
                    <div class="col-12 col-md-6 col-lg-3">
                        <label class="filter-label">Unit <span class="text-danger">*</span></label>
                        <v-select v-model="row.data.unit_id" :options="units" label="name" :reduce="option => option.id"/>
                        <small v-if="errorOf(row, 'unit_id')" class="text-danger">{{ errorOf(row, 'unit_id') }}</small>
                    </div>
                    <div class="col-12 col-md-6 col-lg-3">
                        <label class="filter-label">SKU</label>
                        <input v-model="row.data.sku" type="text" class="form-control">
                    </div>
                    <div class="col-12 col-md-6 col-lg-3">
                        <label class="filter-label">Barcode</label>
                        <input v-model="row.data.barcode_sku" type="text" class="form-control" :class="fieldClass(row, 'barcode_sku')">
                        <small v-if="errorOf(row, 'barcode_sku')" class="text-danger">{{ errorOf(row, 'barcode_sku') }}</small>
                    </div>

                    <div class="col-12 col-md-6 col-lg-3">
                        <label class="filter-label">Purchase A/C <span class="text-danger">*</span></label>
                        <v-select v-model="row.data.purchase_account_head_id" :options="purchaseHeads" label="name" :reduce="option => option.id"/>
                        <small v-if="errorOf(row, 'purchase_account_head_id')" class="text-danger">{{ errorOf(row, 'purchase_account_head_id') }}</small>
                    </div>
                    <div class="col-12 col-md-6 col-lg-3">
                        <label class="filter-label">Sales A/C <span class="text-danger">*</span></label>
                        <v-select v-model="row.data.sales_account_head_id" :options="salesHeads" label="name" :reduce="option => option.id"/>
                        <small v-if="errorOf(row, 'sales_account_head_id')" class="text-danger">{{ errorOf(row, 'sales_account_head_id') }}</small>
                    </div>
                    <div class="col-12 col-md-6 col-lg-3">
                        <label class="filter-label">Cost of Sales A/C <span class="text-danger">*</span></label>
                        <v-select v-model="row.data.cost_of_sales_account_head_id" :options="costHeads" label="name" :reduce="option => option.id"/>
                        <small v-if="errorOf(row, 'cost_of_sales_account_head_id')" class="text-danger">{{ errorOf(row, 'cost_of_sales_account_head_id') }}</small>
                    </div>
                    <div class="col-12 col-md-6 col-lg-3">
                        <label class="filter-label">Inventory A/C <span class="text-danger">*</span></label>
                        <v-select v-model="row.data.inventory_account_head_id" :options="inventoryHeads" label="name" :reduce="option => option.id"/>
                        <small v-if="errorOf(row, 'inventory_account_head_id')" class="text-danger">{{ errorOf(row, 'inventory_account_head_id') }}</small>
                    </div>

                    <div class="col-6 col-md-3 col-lg-3">
                        <label class="filter-label">Purchase Price</label>
                        <input v-model="row.data.purchase_price" type="number" class="form-control">
                    </div>
                    <div class="col-6 col-md-3 col-lg-3">
                        <label class="filter-label">Sales Price</label>
                        <input v-model="row.data.sales_price" type="number" class="form-control">
                    </div>
                    <div class="col-6 col-md-3 col-lg-3">
                        <label class="filter-label">Cost of Sales</label>
                        <input v-model="row.data.cost_of_sales" type="number" class="form-control">
                    </div>
                    <div class="col-6 col-md-3 col-lg-3">
                        <label class="filter-label">VAT Rate</label>
                        <input v-model="row.data.vat_rate" type="number" class="form-control">
                    </div>
                </div>
            </div>

            <p v-if="! rows.length && ! loading" class="empty-note">
                Every synced product in this list has been handled.
            </p>
        </div>
        <Loader v-if="loading"/>
    </div>
</template>

<script setup>
import { ref, computed, inject, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import handleEcommerce from '@/services/modules/ecommerce'
import handleInventory from '@/services/modules/inventory'
import PageHeader from '@/components/atom/PageHeader'
import Loader from '@/components/atom/LoaderComponent'

const route = useRoute()
const router = useRouter()
const showError = inject('showError')
const showSuccess = inject('showSuccess')

const {
    fetchSyncProductLog,
    updateSyncProductRow,
    applySyncProductToAll,
    rejectSyncProductRow,
    restoreSyncProductRow,
    approveSyncProduct
} = handleEcommerce()
const { fetchUnit, fetchGroup, fetchAccountHeads, fetchAccountHeadsBySlags } = handleInventory()

const log = ref({ batch: '', approved_count: 0 })
const rows = ref([])
const units = ref([])
const groups = ref([])
const accountHeads = ref([])
const slagHeads = ref({})
const loading = ref(false)
const bulkOpen = ref(false)
const bulk = ref({})
const pendingPrefill = ref({})

const companyId = computed(() => route.params.companyId)
const logId = computed(() => route.params.uploadLogId)
const referenceLabel = computed(() => {
    if (! log.value.batch) return ''
    return `Reference: ${log.value.batch}`
})
const pendingRows = computed(() => rows.value.filter(row => ! isRejected(row)))
const rejectedRows = computed(() => rows.value.filter(row => isRejected(row)))
const readyCount = computed(() => rows.value.filter(row => isReady(row)).length)
const blockedCount = computed(() => pendingRows.value.length - readyCount.value)
const purchaseHeads = computed(() => headsBySlag('purchase_accounts'))
const salesHeads = computed(() => headsBySlag('sales_accounts'))
const costHeads = computed(() => headsBySlag('cost_of_sales'))
const inventoryHeads = computed(() => headsBySlag('inventory_assets'))

const headsBySlag = (slag) => {
    const heads = slagHeads.value
    if (! heads || ! heads[slag]) return accountHeads.value
    return heads[slag]
}

const headDefaults = () => {
    return {
        purchase_account_head_id: purchaseHeads.value,
        sales_account_head_id: salesHeads.value,
        cost_of_sales_account_head_id: costHeads.value,
        inventory_account_head_id: inventoryHeads.value
    }
}

const prefillHeads = () => {
    const defaults = headDefaults()
    const filled = {}
    rows.value.forEach(row => {
        if (isRejected(row)) return
        Object.keys(defaults).forEach(field => {
            if (row.data[field]) return
            const options = defaults[field]
            if (! options || ! options.length) return
            row.data[field] = options[0].id
            filled[field] = options[0].id
            if (row.validation) delete row.validation[field]
        })
    })
    return filled
}

const persistPrefilledHeads = async (filled) => {
    const fields = Object.keys(filled)
    if (! fields.length) return
    await applySyncProductToAll(logId.value, {
        company_id: companyId.value,
        data: filled
    })
}

const requiredFields = [
    'name',
    'product_head_id',
    'unit_id',
    'purchase_account_head_id',
    'sales_account_head_id',
    'cost_of_sales_account_head_id',
    'inventory_account_head_id'
]

const isRejected = (row) => row.status === 'rejected'

const isReady = (row) => {
    if (isRejected(row)) return false
    const missing = requiredFields.filter(field => ! row.data[field])
    if (missing.length > 0) return false
    if (! row.validation) return true
    return Object.keys(row.validation).length === 0
}

const errorOf = (row, field) => {
    if (! row.validation || ! row.validation[field]) return ''
    return row.validation[field]
}

const fieldClass = (row, field) => {
    if (errorOf(row, field)) return 'is-invalid'
    return ''
}

const cardClass = (row) => {
    if (isRejected(row)) return 'is-rejected'
    if (isReady(row)) return 'is-ready'
    return 'is-blocked'
}

const getLog = async () => {
    try {
        loading.value = true
        const res = await fetchSyncProductLog(logId.value, `?company_id=${companyId.value}`)
        if (! res.status) {
            showError(res.message)
            return
        }
        log.value = res.data
        rows.value = res.data.rows
        pendingPrefill.value = prefillHeads()
    } catch (err) {
        showError('Something went wrong')
    } finally {
        loading.value = false
    }
}

const onSave = async (row) => {
    try {
        loading.value = true
        const res = await updateSyncProductRow(logId.value, {
            company_id: companyId.value,
            batch: row.data.batch,
            data: row.data
        })
        if (! res.status) {
            showError(res.message)
            return
        }
        showSuccess(res.message)
        getLog()
    } catch (err) {
        showError('Something went wrong')
    } finally {
        loading.value = false
    }
}

const onReject = async (row) => {
    if (! confirm('Reject this product? It will not be created in the ERP.')) return
    await changeRowStatus(rejectSyncProductRow, row)
}

const onRestore = async (row) => {
    await changeRowStatus(restoreSyncProductRow, row)
}

const changeRowStatus = async (action, row) => {
    try {
        loading.value = true
        const res = await action(logId.value, {
            company_id: companyId.value,
            batch: row.data.batch
        })
        if (! res.status) {
            showError(res.message)
            return
        }
        showSuccess(res.message)
        getLog()
    } catch (err) {
        showError('Something went wrong')
    } finally {
        loading.value = false
    }
}

const openBulkApply = () => {
    bulk.value = {}
    bulkOpen.value = true
}

const closeBulkApply = () => {
    bulkOpen.value = false
}

const onApplyToAll = async () => {
    const payload = {}
    Object.keys(bulk.value).forEach(key => {
        if (! bulk.value[key]) return
        payload[key] = bulk.value[key]
    })

    if (! Object.keys(payload).length) {
        showError('Select at least one field to apply')
        return
    }

    try {
        loading.value = true
        const res = await applySyncProductToAll(logId.value, {
            company_id: companyId.value,
            data: payload
        })
        if (! res.status) {
            showError(res.message)
            return
        }
        showSuccess(res.message)
        closeBulkApply()
        getLog()
    } catch (err) {
        showError('Something went wrong')
    } finally {
        loading.value = false
    }
}

const onApprove = async () => {
    if (! confirm(`Create ${readyCount.value} product(s) in the ERP?`)) return

    try {
        loading.value = true
        await persistPrefilledHeads(pendingPrefill.value)
        const res = await approveSyncProduct(logId.value, { company_id: companyId.value })
        if (! res.status) {
            showError(res.message)
            return
        }
        pendingPrefill.value = {}
        showSuccess(res.message)
        getLog()
    } catch (err) {
        showError('Something went wrong')
    } finally {
        loading.value = false
    }
}

const goToList = () => {
    router.push({ name: 'sync-ecommerce-product-list', params: route.params, query: route.query })
}

const getDependencies = () => {
    const id = companyId.value
    const slags = ['purchase_accounts', 'sales_accounts', 'cost_of_sales', 'inventory_assets']
    const slagQuery = `?company_id=${id}` + slags.map(slag => `&slags[]=${slag}`).join('')

    return Promise.all([
        fetchUnit(id).then(res => { if (res.data) units.value = res.data }),
        fetchGroup(id, 'finished_goods').then(res => { if (res.data) groups.value = res.data }),
        fetchAccountHeads(id).then(res => { if (res.data) accountHeads.value = res.data }),
        fetchAccountHeadsBySlags(slagQuery).then(res => { if (res.data) slagHeads.value = res.data })
    ]).catch(() => showError('Something went wrong'))
}

onMounted(async () => {
    await getDependencies()
    getLog()
})
</script>


<style scoped>
.status-bar {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    padding: 12px 20px;
    border-bottom: 1px solid #eaecf0;
}
.status-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 24px;
    padding: 0 10px;
    border-radius: 12px;
    border: 1px solid transparent;
    font-size: 12px;
    font-weight: 500;
}
.status-chip b {
    font-weight: 600;
}
.status-chip.is-awaiting {
    background: #fffaeb;
    border-color: #fedf89;
    color: #b54708;
}
.status-chip.is-ready {
    background: #ecfdf3;
    border-color: #abefc6;
    color: #067647;
}
.status-chip.is-blocked {
    background: #fef3f2;
    border-color: #fecdca;
    color: #b42318;
}
.status-chip.is-rejected {
    background: #f9fafb;
    border-color: #e4e7ec;
    color: #101828;
}
.status-chip.is-approved {
    background: #eff8ff;
    border-color: #b2ddff;
    color: #175cd3;
}
.page-head-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 36px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 500;
    padding: 0 16px;
    white-space: nowrap;
}
.page-head-btn:disabled {
    opacity: .5;
    cursor: not-allowed;
}
.page-head-btn-primary {
    background: #7367f0;
    border: 1px solid #7367f0;
    color: #fff;
}
.page-head-btn-primary:hover:not(:disabled) {
    background: #5e50ee;
    border-color: #5e50ee;
    color: #fff;
}
.page-head-btn-ghost {
    border: 1px solid #e4e7ec;
    background: #fff;
    color: #101828;
}
.page-head-btn-ghost:hover:not(:disabled) {
    background: #f9fafb;
    color: #101828;
}
.bulk-panel {
    padding: 16px 20px;
    background: #f9fafb;
    border-bottom: 1px solid #eaecf0;
}
.bulk-title {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: #101828;
}
.bulk-hint {
    margin: 2px 0 12px;
    font-size: 12px;
    color: #101828;
}
.bulk-actions {
    display: flex;
    gap: 8px;
    margin-top: 12px;
}
.cards-section {
    padding: 16px 20px;
}
.product-card {
    border: 1px solid #eaecf0;
    border-left: 3px solid #d0d5dd;
    border-radius: 10px;
    padding: 16px 18px;
    margin-bottom: 12px;
    background: #fff;
}
.product-card.is-ready {
    border-left-color: #17b26a;
}
.product-card.is-blocked {
    border-left-color: #f04438;
    background: #fffbfa;
}
.product-card.is-rejected {
    border-left-color: #d0d5dd;
    background: #f9fafb;
    opacity: .75;
}
.card-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
}
.card-head-left {
    min-width: 0;
}
.card-title-row {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}
.card-title {
    margin: 0;
    font-size: 15px;
    font-weight: 600;
    color: #101828;
}
.card-meta {
    display: block;
    margin-top: 2px;
    font-size: 12px;
    color: #101828;
}
.card-head-actions {
    display: flex;
    gap: 8px;
}
.card-fields {
    margin-top: 14px;
}
.row-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 32px;
    padding: 0 14px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 500;
    white-space: nowrap;
}
.row-btn-primary {
    background: #7367f0;
    border: 1px solid #7367f0;
    color: #fff;
}
.row-btn-primary:hover {
    background: #5e50ee;
    border-color: #5e50ee;
    color: #fff;
}
.row-btn-danger {
    border: 1px solid #fda29b;
    background: #fff;
    color: #d92d20;
}
.row-btn-danger:hover {
    background: #fef3f2;
    color: #b42318;
}
.row-btn-ghost {
    border: 1px solid #e4e7ec;
    background: #fff;
    color: #101828;
}
.row-btn-ghost:hover {
    background: #f9fafb;
    color: #101828;
}
.filter-label {
    display: block;
    margin-bottom: 4px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: .04em;
    text-transform: uppercase;
    color: #101828;
}
.product-card :deep(.form-control),
.bulk-panel :deep(.form-control) {
    border-radius: 8px;
    border-color: #e4e7ec;
    color: #101828;
}
.product-card :deep(.vs__dropdown-toggle),
.bulk-panel :deep(.vs__dropdown-toggle) {
    border-radius: 8px;
    border-color: #e4e7ec;
}
.product-card :deep(.vs__selected),
.bulk-panel :deep(.vs__selected) {
    color: #101828;
}
.text-muted {
    color: #101828 !important;
}
:deep(.page-subtitle) {
    color: #101828;
}
.empty-note {
    padding: 32px 0;
    text-align: center;
    font-size: 13px;
    color: #101828;
}
</style>
